// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyAdmin } from "@/types";
import { rightClickToOpen } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import Avatar from "@/UI/atoms/Avatar";
import StatusChip from "@/UI/atoms/StatusChip";
import "../row.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    admin: TyAdmin;
    /** The signed-in account's own row. It keeps its chips and loses its actions. */
    isMe?: boolean;
    /** The only active super left. The API refuses to demote or deactivate them, so the UI says why. */
    isLastSuper?: boolean;
    onEdit: () => void;
    onActions: (anchor: DOMRect) => void;
};

/**
 * One of Lozicore's own team. There are no admin roles — an admin either is super or is not, and the
 * only thing that turns on it is managing this very list.
 *
 * The card opens nothing: an admin has no screen of its own and does not need one, so editing is the
 * main action rather than a menu item.
 *
 * **Your own row keeps Edit and loses the menu.** Everything behind that menu is something the API
 * refuses to do to yourself — demote, deactivate, issue a password — and a button that exists to be
 * refused is a worse answer than no button. Your name and your email are still yours to change, and
 * your password is on your account page.
 */
export default function AdminCard({ admin, isMe, isLastSuper, onEdit, onActions }: Props) {
    const t = useTranslation();

    return (
        <article className="row-card" onContextMenu={rightClickToOpen(onActions)}>
            <Avatar name={admin.name} picture={admin.picture} size={42} bordered />

            <div className="row-card__body">
                <span className="row-card__title">{admin.name}</span>
                <span className="row-card__meta row-card__ltr">{admin.phone}</span>
                {admin.email && <span className="row-card__meta row-card__ltr">{admin.email}</span>}
            </div>

            <div className="row-card__chips">
                {isMe && <StatusChip size="sm" tone="accent" label={t.admins.me} />}
                {admin.isSuper && <StatusChip size="sm" tone="accent" label={t.admins.super} />}
                <StatusChip size="sm" tone={admin.isActive ? "good" : "neutral"} label={admin.isActive ? t.admins.active : t.admins.suspended} />
                {admin.isPasswordTemporary && <StatusChip size="sm" tone="warn" label={t.password.temporary} />}
            </div>

            <div className="row-card__actions">
                <button type="button" className="row-card__main" onClick={onEdit}>
                    <Icon name="edit" size={16} />
                    <span>{t.common.edit}</span>
                </button>

                {!isMe && (
                    <button
                        type="button"
                        className="row-card__more"
                        onClick={(event) => onActions(event.currentTarget.getBoundingClientRect())}
                        aria-label={t.common.more}
                    >
                        <Icon name="more" size={18} />
                    </button>
                )}
            </div>

            {/* Said on the row rather than discovered by pressing something that then refuses. */}
            {isLastSuper && !isMe && <p className="admin-card__locked">{t.admins.lastSuperHint}</p>}
        </article>
    );
}
